import cloneDeep from 'lodash/cloneDeep';

import { ActionTree, MutationTree } from 'vuex';
import { RootState } from '@/store/types';
import { ServerResult } from '@/core/http/server_result';
import { fetchEnumsFromSchema } from '@/core/tools/forms/fetch_enums_from_schema';
import { generateFormObject } from '@/core/tools/forms/generate_form_object';
import { prepareFormObjectForSave } from '@/core/tools/forms/prepare_form_object_for_save';

import { buildSemifinishedRelationsDiff } from '../utils/semifinishedRelations';

interface CounterpartyFormState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;

  document: any;
  product_rels: any[];
  targetForDelete: any[];
  form: any;
  schema: any;

  semifinishedRows: any[];
  savedSemifinishedRows: any[];

  meta: any;
}

const state: CounterpartyFormState = {
  loading: false,
  loaded: false,
  fatalError: false,

  document: null,
  product_rels: [],
  targetForDelete: [],
  form: null,
  schema: null,

  semifinishedRows: [],
  savedSemifinishedRows: [],

  meta: {},
};

const initState: CounterpartyFormState = cloneDeep(state);

export const PRODUCT_DATA = 'GET_PRODUCT_DATA';
export const PRODUCT_RELS_DATA = 'GET_PRODUCT_RELS_DATA';
export const PRODUCT_SCHEMA = 'GET_PRODUCT_SCHEMA';
export const PRODUCT_SEMIFINISHEDS = 'GET_PRODUCT_SEMIFINISHEDS';

export const actions: ActionTree<CounterpartyFormState, RootState> = {
  reset({ commit }) {
    commit('RESET_STATE')
  },

  init({ commit, dispatch }, params) {
    return dispatch('loadData', params);
  },

  loadData({ commit }, params) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit('START_LOADING');

      vm.$db([
        vm.$q(PRODUCT_DATA)
          .raw('documents.get')
          .action({
            id: params.id
          }),
        vm.$q(PRODUCT_SEMIFINISHEDS)
          .raw('documents.list')
          .action({
            columns: {
              id: 'product_semifinished_products.id',
              product_id: 'product_semifinished_products.product_id',
              semifinished_product_id: 'product_semifinished_products.semifinished_product_id',
              consumption_percent: 'product_semifinished_products.consumption_percent',
              name: 'semifinished_products.name'
            },
            from: 's:product_semifinished_products',
            from_as: 'product_semifinished_products',
            joins: [
              {
                join: 's:products',
                as: 'semifinished_products',
                on: {
                  $eq: [
                    'semifinished_products.id',
                    'product_semifinished_products.semifinished_product_id'
                  ]
                }
              }
            ],
            where: {
              $eq: ['product_semifinished_products.product_id', { $: params.id }]
            },
            page_size: 100000
          }),
      ]).then((result: ServerResult) => {
        const content = result.getOperation(PRODUCT_DATA).getContent();
        const semifinishedRows =
          (result.getOperation(PRODUCT_SEMIFINISHEDS).getContent() || {})
            .documents || [];

        console.debug('[ProductForm.loadData] semifinished rows loaded', {
          productId: params.id,
          count: semifinishedRows.length
        });

        commit('SET_FORM_DATA', {
          ...content,
          semifinishedRows
        });

        commit('LOADED_SUCCESS');
        resolve(null);
      })
        .catch((error: any) => {
          console.log('error', error)
          commit('LOADED_ERROR');
          reject()
        })
    })
  },

  prepareNew({ commit, state }, initParams) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit('START_LOADING');

      vm.$db([
        vm.$q(PRODUCT_SCHEMA)
          .raw('schemas.get')
          .action({
            id: `s:products`
          }),
      ]).then((result: ServerResult) => {
        commit('GENERATE_FORM_DATA', {
          ...result.getOperation(PRODUCT_SCHEMA).getContent(),
          initParams
        });

        commit('LOADED_SUCCESS');
        resolve();
      })
        .catch((error: any) => {
          console.log('error', error)
          commit('LOADED_ERROR');
          reject()
        })
    })
  },

  saveForm({ commit }, documentId) {
    const vm = (this as any)._vm;

    const normalizedForm = prepareFormObjectForSave(state.schema, state.form);

    // Состав полуфабрикатов — many-relation, сохраняется отдельным diff'ом.
    // При установленном флаге is_semifinished таблица скрыта: существующие
    // строки не трогаем (вернутся при снятии флага).
    const skipSemifinishedRelations = !!state.form.is_semifinished;
    const diff = skipSemifinishedRelations
      ? { toCreate: [], toUpdate: [], toDelete: [] }
      : buildSemifinishedRelationsDiff(
          state.savedSemifinishedRows,
          state.semifinishedRows
        );

    console.debug('[ProductForm.saveForm] semifinished diff', {
      documentId,
      is_semifinished: skipSemifinishedRelations,
      create: diff.toCreate.length,
      update: diff.toUpdate.length,
      delete: diff.toDelete.length
    });

    // DocAPI decimal-cast принимает string/float/Decimal, но НЕ integer —
    // поэтому процент уходит строкой ("50", "12.5")
    const createRelatedRow = (createRelated: any) => (row: any) =>
      createRelated({
        semifinished_product_id: row.semifinished_product_id,
        consumption_percent: String(row.consumption_percent)
      });

    return new Promise((resolve, reject) => {
      commit('START_LOADING');

      let saveQuery = null;

      if (documentId) {
        saveQuery = vm.$q('PRODUCT_DATA_SAVE')
          .update(documentId, normalizedForm)

        if (!skipSemifinishedRelations) {
          saveQuery = saveQuery
            .createManyRelation(
              'product_semifinished_products',
              'product_id',
              (createRelated: any) =>
                diff.toCreate.map(createRelatedRow(createRelated))
            )
            .updateManyRelation(
              'product_semifinished_products',
              'product_id',
              (updateRelated: any) =>
                diff.toUpdate.map((row: any) =>
                  updateRelated({
                    id: row.id,
                    consumption_percent: String(row.consumption_percent)
                  })
                )
            )
            .deleteManyRelation(
              'product_semifinished_products',
              'product_id',
              (deleteRelated: any) => diff.toDelete.map((row: any) => deleteRelated(row))
            );
        }
      } else {
        saveQuery = vm.$q('PRODUCT_DATA_SAVE')
          .create('s:products', normalizedForm)

        if (!skipSemifinishedRelations && diff.toCreate.length > 0) {
          saveQuery = saveQuery.withManyRelation(
            'product_semifinished_products',
            'product_id',
            (createRelated: any) =>
              diff.toCreate.map(createRelatedRow(createRelated))
          );
        }
      }

      vm.$db([
        saveQuery
      ]).then((result: ServerResult) => {
        const operationResult = result.getOperation('PRODUCT_DATA_SAVE');

        if (operationResult.hasError()) {
          console.log('error', operationResult.getContent());

          commit('LOADED_SUCCESS');
          reject();
        } else {
          commit('ON_FORM_SAVE', operationResult.getContent())

          commit('LOADED_SUCCESS');
          resolve(operationResult.getContent());
        }
      })
        .catch((error: any) => {
          console.error('errror', error)
          reject(error);
        });
    })
  },

  changeFormValue({ commit }, { field, newValue }) {
    commit('CHANGE_VALUE', { field, newValue });
  },

  addSemifinishedRow({ commit, state }, { product }) {
    if (!product || product.id == null) {
      return false;
    }

    const exists = state.semifinishedRows.some(
      (row: any) => row.semifinished_product_id == product.id
    );
    if (exists) {
      console.debug('[ProductForm.addSemifinishedRow] duplicate skipped', {
        id: product.id
      });
      return false;
    }

    commit('SEMIFINISHED_ROW_ADD', {
      row: {
        semifinished_product_id: product.id,
        name: product.name || product.label,
        consumption_percent: null
      }
    });

    console.debug('[ProductForm.addSemifinishedRow] added', {
      id: product.id,
      total: state.semifinishedRows.length
    });
    return true;
  },

  removeSemifinishedRow({ commit }, { index }) {
    console.debug('[ProductForm.removeSemifinishedRow]', { index });
    commit('SEMIFINISHED_ROW_REMOVE', { index });
  },

  changeSemifinishedRowValue({ commit }, { index, newValue }) {
    commit('SEMIFINISHED_ROW_CHANGE', { index, newValue });
  },

  addAttachments({ state, dispatch }, { newValue }) {
    state.form.attachments = [
      newValue,
      ...(state.form.attachments ? state.form.attachments : []),
    ]
  },
};

export const mutations: MutationTree<CounterpartyFormState> = {
  RESET_STATE: (state) => {
    for (const key in initState) {
      (state as any)[key] = (initState as any)[key]
    }
  },

  START_LOADING: (state) => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },

  LOADED_SUCCESS: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },

  LOADED_ERROR: (state) => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  SET_FORM_DATA(state, { document, schema, semifinishedRows }) {
    state.document = document;
    state.schema = schema;
    state.meta = fetchEnumsFromSchema(schema);

    state.form = generateFormObject(schema, state.document);

    state.savedSemifinishedRows = cloneDeep(semifinishedRows || []);
    state.semifinishedRows = cloneDeep(semifinishedRows || []);
  },

  GENERATE_FORM_DATA(state, { schema, initParams }) {
    state.document = {};
    state.schema = schema;
    state.meta = fetchEnumsFromSchema(schema);

    state.form = generateFormObject(schema, initParams);
  },

  ON_FORM_SAVE(state, { document, schema }) {
    state.document = document;
    state.schema = schema;

    state.form = generateFormObject(schema, state.document);
  },

  CHANGE_VALUE(state: CounterpartyFormState, { field, newValue }) {
    switch (field) {
      default:
        state.form[field] = newValue;
    }
  },

  SEMIFINISHED_ROW_ADD(state: CounterpartyFormState, { row }) {
    state.semifinishedRows = [...state.semifinishedRows, row];
  },

  SEMIFINISHED_ROW_REMOVE(state: CounterpartyFormState, { index }) {
    state.semifinishedRows = state.semifinishedRows.filter(
      (_row: any, i: number) => i !== index
    );
  },

  SEMIFINISHED_ROW_CHANGE(
    state: CounterpartyFormState,
    { index, newValue }
  ) {
    const rows = [...state.semifinishedRows];
    rows[index] = { ...rows[index], consumption_percent: newValue };
    state.semifinishedRows = rows;
  },
};

const getters = {
  fields: (state: CounterpartyFormState) => {
    return state.schema.fields.reduce((prev: any, field: any) => {
      prev[field.code] = field;
      return prev;
    }, {});
  },
};

export const STORE_KEY = '$_product_directory_product_form';

export const store = {
  namespaced: true,
  state,
  actions,
  getters,
  mutations,
};
